<template>
  <div class="table-component">
    <!--loader-->
    <div class="loader" v-if="loading">
      <span aria-hidden="true" class="spinner-border" />
    </div>

    <div v-else>
      <div class="tab__content__top" v-if="!hideTableTop">
        <div class="tab__content__top__left tab__content_flex">
          <svg
            v-if="toggleContent && !showContents"
            @click="showContents = true"
            width="7"
            height="12"
            viewBox="0 0 7 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 11L6 6L1 1"
              stroke="#e00b5b"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            v-if="toggleContent && showContents"
            @click="showContents = false"
            width="13"
            height="8"
            viewBox="0 0 13 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.5 1.5L6.5 6.5L11.5 1.5"
              stroke="#e00b5b"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <p v-if="!loading">
            {{ title }}
          </p>
        </div>
        <slot v-if="showContents || showFilterSlot" name="filter"></slot>
        <div class="tab__content__top__right" v-if="!hideFilter">
          <Button
            buttonName="Clear Filter"
            :hideButton="showFilter && !openFilters"
            :onClick="clearFilterHandler"
            classes="basic-borderless"
          />
          <Button
            button-name="Filter"
            :hide-button="!showFilter"
            :on-click="() => (openFilters = true)"
            classes="basic"
          />
          <div v-if="showExport" class="show-export">
            <Button
              buttonName="Export"
              :disabled="disableExport || !contents.length"
              :onClick="() => (showExportOptions = !showExportOptions)"
              classes="basic"
            >
              <span>
                <svg
                  fill="none"
                  height="12"
                  viewBox="0 0 14 18"
                  width="14"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.25 0V12.4219L2.28906 8.46094L1.21094 9.53906L6.46094 14.7891L7 15.3047L7.53906 14.7891L12.7891 9.53906L11.7109 8.46094L7.75 12.4219V0H6.25ZM0.25 16.5V18H13.75V16.5H0.25Z"
                    fill="#e00b5b"
                  />
                </svg>
              </span>
            </Button>
            <div class="export-options" v-if="showExportOptions">
              <div class="export-options__option" @click="exportFile('pdf')">
                PDF
              </div>
              <div class="export-options__option" @click="exportFile('csv')">
                CSV
              </div>
            </div>
          </div>
          <Button
            v-if="showPrimaryButton"
            :button-name="primaryButtonName"
            :disabled="handlePrimaryButtonDisabled"
            :on-click="handlePrimaryButtonClick"
            classes="primary"
          />
        </div>
      </div>
      <slot :openFilters="openFilters" :data="state" />
      <slot v-if="!loading" name="tab" />

      <!--table-->
      <div class="table card-table" v-if="showTableContent">
        <vue-good-table
          :columns="tableColumns"
          :rows="tableRows"
          style-class="vgt-table"
          :select-options="{
            enabled: useCheckbox,
            selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
            selectionText: checkboxSelectionText
              ? checkboxSelectionText
              : 'rows selected',
            clearSelectionText: 'clear',
            selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
          }"
          :search-options="{
            enabled: useSearch,
            trigger: 'enter',
            skipDiacritics: true,
            placeholder: searchBarPlaceholder
              ? searchBarPlaceholder
              : 'Search this table',
          }"
          @on-selected-rows-change="
            handleCheckboxSelected ? handleCheckboxSelected($event) : ''
          "
          @on-row-click="handleRowClick ? handleRowClick($event) : ''"
          @on-cell-click="handleCellClick ? handleCellClick($event) : ''"
        >
          <template v-slot:table-row="tableData">
            <span v-if="tableData.column.field.toLowerCase() == 'status'">
              <status-indicator :type="tableData.row.STATUS" />
            </span>

            <span
              v-else-if="
                tableData.column.field.toLowerCase() == 'action-slot-1'
              "
            >
              <slot name="action-slot-1" :slotData="tableData.row" />
            </span>

            <span
              v-else-if="
                tableData.column.field.toLowerCase() == 'action-slot-2'
              "
            >
              <slot name="action-slot-2" :slotData="tableData.row" />
            </span>

            <span
              v-else-if="
                tableData.column.field.toLowerCase() == 'action-slot-3'
              "
            >
              <slot name="action-slot-3" :slotData="tableData.row" />
            </span>

            <span v-else-if="tableData.column.splitBy">
              {{
                tableData.row[tableData.column.field].split(
                  tableData.column.splitBy
                )[0]
              }}
              <br />
              <span
                class="my-2 d-inline-block"
                v-if="
                  tableData.row[tableData.column.field].split(
                    tableData.column.splitBy
                  )[1]
                "
              >
                {{
                  tableData.row[tableData.column.field]
                    .split(tableData.column.splitBy)
                    .slice(1, 3)
                    .join(" ")
                }}
              </span>
            </span>
          </template>
        </vue-good-table>
      </div>
      <div class="table__details" v-if="showTableContent && !hidePagination">
        <div class="table__details__left">
          <span class="text-muted"
            >Page {{ pageInfo.current_page }} of
            {{ pageInfo.total_pages }}</span
          >
        </div>
        <div class="table__details__right">
          <Pagination
            :number-of-pages="pageInfo.total_pages"
            :current-page="currentPage"
            :useQuery="useQuery"
            :handlePageChange="handlePageChange"
          />
        </div>
        <div class="table__details__right" />
      </div>
      <div v-if="!contents.length && !loading">
        <slot name="noContent" />
      </div>
      <div v-if="showFooter">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>
<script>
import "vue-good-table/dist/vue-good-table.css";
import { VueGoodTable } from "vue-good-table";
import ExportButton from "@/components/export-button.vue";
import Button from "@/components/button.vue";
import Pagination from "@/components/pagination.vue";
import { reactive, toRefs, computed } from "@vue/composition-api";
import StatusIndicator from "./status-indicator";

export default {
  components: {
    StatusIndicator,
    Button,
    ExportButton,
    Pagination,
    VueGoodTable,
  },

  props: {
    clearFilter: Function,
    columns: Array,
    contents: Array,
    currentPage: Number,
    handleExportDownload: Function,
    handlePrimaryButtonClick: Function,
    showPrimaryButton: {
      type: Boolean,
      default: true,
    },
    handlePrimaryButtonDisabled: Boolean,
    hideTableTop: Boolean,
    hidePagination: Boolean,
    loading: Boolean,
    onRowChecked: Function,
    pageInfo: Object,
    primaryButtonDisabled: Boolean,
    primaryButtonName: String,
    rows: Array,
    showExport: Boolean,
    disableExport: Boolean,
    showFilter: Boolean,
    showFooter: Boolean,
    title: String,
    useCheckbox: Boolean,
    checkboxSelectionText: String,
    handleCheckboxSelected: Function,
    handleRowClick: Function,
    useSearch: Boolean,
    searchBarPlaceholder: Boolean,
    multiLineProperties: Array,
    showFilterSlot: Boolean,
    hideFilter: Boolean,
    handleCellClick: Function,
    toggleContent: Boolean,
    onPageChange: Function,
    noClickEvent: Boolean,
    useQuery: {
      type: Boolean,
      default: true,
    },
    handlePageChange: {
      type: Function,
      default: () => {},
    },
    paramsObject: Object,
  },

  setup(props) {
    const state = reactive({
      showExportOptions: false,
      exportDisabled: false,
      openFilters: false,
      showContents: false,
    });

    const tableColumns = computed(() => {
      if (props.columns) return props.columns;
      else if (props.contents[0]) {
        return Object.keys(props.contents[0]).map((title) => {
          return {
            label: title,
            field: title,
          };
        });
      } else return [];
    });

    const showTableContent = computed(() => {
      return (
        props.contents.length &&
        ((!props.loading && !props.toggleContent) ||
          (props.toggleContent && state.showContents && !props.loading))
      );
    });

    const tableRows = computed(() =>
      props.rows ? props.rows : props.contents
    );

    const exportFile = (type) => {
      props.handleExportDownload(type);
      state.showExportOptions = false;
      state.exportDisabled = state.showExportOptions;
    };

    const onChecked = (e) => {
      props.onRowChecked(e);
    };

    const clearFilterHandler = () => {
      state.openFilters = false;
      props.clearFilter();
    };

    return {
      ...toRefs(state),
      state,
      clearFilterHandler,
      exportFile,
      showTableContent,
      onChecked,
      tableColumns,
      tableRows,
    };
  },
};
</script>

<style lang="scss" scoped>
.table-component {
  margin: 0 20px;
  background: #ffffff;
  box-sizing: border-box;
  border-radius: 6px;

  p {
    line-height: 24px;
  }

  svg {
    cursor: pointer;
  }

  .show-export {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;

    .export-options {
      background: #fff;
      z-index: 100;
      padding: 10px;
      box-shadow: 0 0 0 0.05px;
      position: absolute;
      bottom: -85px;
      width: 93px;
      right: 0;
      border: 1px solid #dfe2e5;
      border-radius: 4px;

      &__option {
        padding: 8px 0;
        color: #e00b5b;
        font-size: 12px;
        cursor: pointer;
      }
    }
  }

  .darken {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    color: #261940;
  }

  .tab {
    &__content {
      td {
        span {
          font-style: normal;
          font-weight: normal;
          font-size: 14px;
          line-height: 20px;
          color: rgba(38, 25, 64, 0.6);
        }

        .time {
          font-size: 12px;
        }
      }

      &__top {
        &__right {
          .button-comp {
            margin-left: 20px;
          }
        }
      }

      .checkbox-svg {
        cursor: pointer;
      }

      .upper-text {
        font-style: normal;
        font-weight: normal;
        font-size: 10px;
        line-height: 20px;
        color: rgba(38, 25, 64, 0.6);
      }
      &__top__item {
        display: flex;
        align-items: center;
      }

      .green {
        background-color: #f5fcfb;
      }

      ul {
        margin: 0;
        padding: 0;
      }

      input {
        max-width: 120px;
      }

      .pending {
        color: #ff9a02;
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 20px;
      }

      .active {
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 20px;
        color: #e00b5b;
      }

      .inactive {
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 20px;
        text-align: right;
        color: #f36b7f;
      }

      .type {
        font-style: normal;
        font-weight: bold;
        font-size: 14px;
        line-height: 20px;
        color: rgba(38, 25, 64, 0.6);

        span {
          border-radius: 15px;
          font-style: normal;
          font-weight: normal;
          font-size: 14px;
          line-height: 20px;
          color: rgba(38, 25, 64, 0.6);
          text-align: center;
          padding: 5px;
        }
      }
    }
  }
}
.display-none {
  display: none;
}
.small-width {
  max-width: 35px !important;
}

.tab__content_flex {
  display: flex;
  align-items: baseline;

  p {
    margin-left: 15px;
  }
}
</style>
<style lang="scss">
.vgt-responsive {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none;
  }
}

table.vgt-table {
  border: none;

  th {
    color: #59595999;
    padding-top: 16px;
    padding-bottom: 16px;
    border: none;

    &.sortable span {
      padding-left: 12px;
    }

    &.sortable button {
      top: 1px;
      &:before,
      &:after {
        left: 6px;
      }
    }

    &:first-of-type {
      padding-left: 16px;
      & button {
        &:before,
        &:after {
          left: 14px;
        }
      }
    }

    &:last-child {
      text-align: left;
    }
  }

  td {
    vertical-align: inherit !important;
    padding: 20px 24px 20px 0.75rem !important;
    color: #655d79;
    border-bottom: 1px solid #ebf0f4;

    &:first-of-type {
      padding-left: 1rem !important;
    }

    &:last-child {
      text-align: left;
    }
  }
}
</style>
